import React, { useEffect } from "react";
import "./App.css";

import logo from "./assets/logo.svg";               // 500×500 square logo
import phoneMock from "./assets/phone-mock.png";    // PNG phone mockup
import googlePlay from "./assets/google-play.svg";  // SVG badge
import appStore from "./assets/app-store.svg";      // SVG badge
import vendorSvg from "./assets/vendor.svg";        // 🔹 NEW: Vendor SVG

/* Store links */
const PLAY_STORE =
  "https://play.google.com/store/apps/details?id=com.amar_mahallah.amar_mahallah";
const APP_STORE =
  "https://apps.apple.com/ng/app/amar-mahallah/id6737305293";

/* Vendor partner app (Join as a Vendor) */
const VENDOR_APP =
  "https://play.google.com/store/apps/details?id=com.amar_mahallah.amar_mahallah_partner_management";

/* OPTIONAL: Deep link schemes (configure in your Android/iOS apps) */
const ANDROID_DEEP_LINK = "amarmahallah://home";
const IOS_DEEP_LINK = "amarmahallah://home";

/* ------------------ Helper: detect platform ------------------ */
function detectPlatform() {
  if (typeof navigator === "undefined") {
    return { isAndroid: false, isIOS: false, isMobile: false };
  }

  const ua = navigator.userAgent || navigator.vendor || window.opera;

  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);

  return {
    isAndroid,
    isIOS,
    isMobile: isAndroid || isIOS,
  };
}

/* ------------------ OS Detection Page (/os_detection) ------------------ */
function OsDetectionPage() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const { isAndroid, isIOS, isMobile } = detectPlatform();

    const params = new URLSearchParams(window.location.search);
    // e.g. https://amarmahallah.com/os_detection?no_redirect=1 (for debugging)
    if (params.get("no_redirect") === "1") return;

    // If not mobile, just send to homepage
    if (!isMobile) {
      window.location.replace("/");
      return;
    }

    let deepLink = null;
    let storeLink = null;

    if (isAndroid) {
      deepLink = ANDROID_DEEP_LINK;
      storeLink = PLAY_STORE;
    } else if (isIOS) {
      deepLink = IOS_DEEP_LINK;
      storeLink = APP_STORE;
    } else {
      // Unknown mobile – fallback to homepage
      window.location.replace("/");
      return;
    }

    const start = Date.now();

    // 1️⃣ Try to open the app first (deep link)
    window.location.href = deepLink;

    // 2️⃣ After a short delay, if we are still here, go to the store
    const timeout = setTimeout(() => {
      const elapsed = Date.now() - start;
      if (elapsed < 1600) {
        window.location.href = storeLink;
      }
    }, 1400);

    // Cleanup (not strictly needed but nice for React)
    return () => clearTimeout(timeout);
  }, []);

  // Minimal UI while redirecting
  return (
    <main className="os-detect">
      <div className="os-detect__inner">
        <p>Detecting your device and opening Amar Mahallah…</p>
      </div>
    </main>
  );
}

/* ------------------ Home Page (/) — your existing hero ------------------ */
function HomePage() {
  return (
    <main className="hero">
      <div className="hero__inner">
        {/* LEFT: logo + copy + CTAs */}
        <section aria-label="Amar Mahallah intro">
          <div className="brandbar">
            <img
              className="logo"
              src={logo}
              alt="Amar Mahallah"
              width={220}
              height={220}
            />
          </div>

          <h1 className="title">
            Your hyperlocal marketplace — right in your Mahallah.
          </h1>

          <p className="subtitle">
            Amar Mahallah brings neighborhood shops and services to your
            fingertips—groceries, home-cooked meals, crafts, electronics, and
            more. We match you first to vendors in your Mahallah; if unavailable,
            we connect you to the next closest—keeping it local.
          </p>

          <div className="actions" aria-label="Get Amar Mahallah">
            <a
              className="store-btn"
              href={PLAY_STORE}
              target="_blank"
              rel="noreferrer"
              aria-label="Get it on Google Play"
            >
              <img
                className="store-img"
                src={googlePlay}
                alt="Get it on Google Play"
              />
            </a>

            <a
              className="store-btn"
              href={APP_STORE}
              target="_blank"
              rel="noreferrer"
              aria-label="Download on the App Store"
            >
              <img
                className="store-img"
                src={appStore}
                alt="Download on the App Store"
              />
            </a>

            {/* 🔹 Replaced text button with vendor.svg */}
            <a
              className="vendor-btn"
              href={VENDOR_APP}
              target="_blank"
              rel="noreferrer"
              aria-label="Join as a Vendor"
            >
              <img src={vendorSvg} alt="Join as a Vendor" />
            </a>
          </div>

          <div className="footer">
            © {new Date().getFullYear()} Amar Mahallah — Think Local. Live
            Better.
          </div>
        </section>

        {/* RIGHT: phone mockup */}
        <section className="visual" aria-label="App preview">
          <img
            className="mock"
            src={phoneMock}
            alt="Amar Mahallah app preview on phone"
          />
        </section>
      </div>
    </main>
  );
}

/* ------------------ Root App: choose view by pathname ------------------ */
export default function App() {
  const path =
    typeof window !== "undefined" ? window.location.pathname : "/";

  // Handle both "/os_detection" and "/os_detection/"
  if (path === "/os_detection" || path === "/os_detection/") {
    return <OsDetectionPage />;
  }

  // Default: homepage
  return <HomePage />;
}
